import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Select,
  Tag,
  Space,
  Tabs,
  Typography,
  Spin,
  Input,
  Row,
  Col,
  Badge,
  Modal,
  Form,
  Checkbox,
  message,
  Statistic,
  Tooltip,
  Divider,
  DatePicker
} from 'antd';
import moment from 'moment';
import {
  PushpinOutlined,
  UserOutlined,
  PrinterOutlined,
  SendOutlined,
  FilterOutlined,
  InboxOutlined,
  CarOutlined,
  SyncOutlined,
  FileTextOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import { deliveryPersonsApi, ordersApi } from '../utils/api';

const { Title, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;
const { Search } = Input;

const OrderAssignment = () => {
  // États
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [deliveryPersons, setDeliveryPersons] = useState([]);
  const [zones, setZones] = useState([]);
  const [selectedZone, setSelectedZone] = useState('all');
  const [selectedDeliveryPerson, setSelectedDeliveryPerson] = useState(null);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [assignmentModalVisible, setAssignmentModalVisible] = useState(false);
  const [printModalVisible, setPrintModalVisible] = useState(false);
  const [messageModalVisible, setMessageModalVisible] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [messageForm] = Form.useForm();
  const [assignmentForm] = Form.useForm();
  const [deliveryPersonsStats, setDeliveryPersonsStats] = useState({});
  // États pour la modale des détails
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  // Chargement initial des données
  useEffect(() => {
    fetchData();
  }, []);

  // Chargement des commandes en fonction de la zone sélectionnée
  useEffect(() => {
    fetchOrders();
  }, [selectedZone]);

  // Chargement des statistiques des livreurs
  useEffect(() => {
    if (deliveryPersons.length > 0) {
      fetchDeliveryPersonsStats();
    }
  }, [deliveryPersons]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // // Charger les zones
      // const zonesResponse = await ordersApi.getZones();
      // console.log('zone',zonesResponse);
      // setZones(zonesResponse.data);

      // Charger les livreurs actifs
      const deliveryPersonsResponse = await deliveryPersonsApi.getAll({ is_active: true });
      setDeliveryPersons(deliveryPersonsResponse.data);

      // Charger les commandes prêtes à livrer
      // L'appel à fetchOrders est géré par le useEffect dépendant de selectedZone
      // await fetchOrders(); 
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      message.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const filters = { 
        status: 'new',              // Filtrer uniquement les commandes "new"
        order_by: 'delivery_date', // Tri par date de livraison
        ascending: false,          // Décroissant
        include_items: false,      // Ne pas inclure les items initialement
        exclude_source: 'pos'      // Exclure les commandes venant du POS
      };
      
      if (selectedZone !== 'all') {
        filters.zone_id = selectedZone;
      }
      
      const ordersResponse = await ordersApi.getAll(filters);
      // Mettre à jour directement avec les données de l'API (le filtrage est fait côté serveur)
      setOrders(ordersResponse?.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des commandes:', error);
      message.error('Erreur lors du chargement des commandes');
    } finally {
      setLoading(false);
    }
  };

  const fetchDeliveryPersonsStats = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const statsPromises = deliveryPersons.map(person => 
        deliveryPersonsApi.getDailyStats(person.id, today)
      );
      
      const statsResults = await Promise.all(statsPromises);
      
      const statsMap = {};
      statsResults.forEach((result, index) => {
        if (result && result.data) {
          statsMap[deliveryPersons[index].id] = result.data;
        } else {
          statsMap[deliveryPersons[index].id] = { orders_count: 0 };
        }
      });
      
      setDeliveryPersonsStats(statsMap);
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
    }
  };

  const handleZoneChange = (value) => {
    setSelectedZone(value);
  };

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const handleRefresh = () => {
    fetchOrders();
    fetchDeliveryPersonsStats();
  };

  const handleOrderSelection = (selectedRowKeys) => {
    setSelectedOrders(selectedRowKeys);
  };

  const handleDeliveryPersonSelection = (personId) => {
    setSelectedDeliveryPerson(personId);
  };

  const showAssignmentModal = () => {
    if (selectedOrders.length === 0) {
      message.warning('Veuillez sélectionner au moins une commande');
      return;
    }
    
    // Vérifier que toutes les commandes sélectionnées ont le statut "new"
    const selectedOrdersData = orders.filter(order => selectedOrders.includes(order.id));
    const nonNewOrders = selectedOrdersData.filter(order => order.status !== 'new');
    
    if (nonNewOrders.length > 0) {
      message.warning('Seules les commandes avec le statut "Nouveau" peuvent être attribuées à un livreur');
      return;
    }
    
    // Initialiser le formulaire avec les valeurs par défaut
    assignmentForm.setFieldsValue({
      delivery_person_id: selectedDeliveryPerson,
      delivery_date: '',
      shipping_fee: 0
    });
    
    setAssignmentModalVisible(true);
  };

  const handleAssign = async () => {
    try {
      const values = await assignmentForm.validateFields();
      if (!values.delivery_person_id) {
        message.warning('Veuillez sélectionner un livreur');
        return;
      }

      setLoading(true);
      await ordersApi.assignOrders(
        selectedOrders, 
        values.delivery_person_id,
        {
          delivery_date: values.delivery_date && values.delivery_date.format('YYYY-MM-DD'),
          shipping_fee: values.shipping_fee
        }
      );
      message.success(`${selectedOrders.length} commande(s) attribuée(s) avec succès`);
      setAssignmentModalVisible(false);
      setSelectedOrders([]);
      fetchOrders();
      fetchDeliveryPersonsStats();
    } catch (error) {
      console.error('Erreur lors de l\'attribution des commandes:', error);
      message.error('Erreur lors de l\'attribution des commandes');
    } finally {
      setLoading(false);
    }
  };

  const showPrintModal = () => {
    if (selectedOrders.length === 0) {
      message.warning('Veuillez sélectionner au moins une commande');
      return;
    }
    setPrintModalVisible(true);
  };

  const handlePrint = () => {
    // Générer le document à imprimer
    const selectedOrdersData = orders.filter(order => selectedOrders.includes(order.id));
    const printWindow = window.open('', '_blank');
    
    if (printWindow) {
      const html = generatePrintableContent(selectedOrdersData);
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      
      // On ne ferme pas la fenêtre après impression pour permettre à l'utilisateur de voir le résultat
      setPrintModalVisible(false);
    } else {
      message.error('Impossible d\'ouvrir la fenêtre d\'impression. Vérifiez les paramètres de votre navigateur.');
    }
  };

  const generatePrintableContent = (ordersData) => {
    const deliveryPerson = selectedDeliveryPerson ? 
      deliveryPersons.find(p => p.id === selectedDeliveryPerson) : null;
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Liste de livraison</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          .header { text-align: center; margin-bottom: 20px; }
          .delivery-info { margin-bottom: 20px; border: 1px solid #ccc; padding: 10px; }
          table { width: 100%; border-collapse: collapse; }
          th, td { padding: 8px; text-align: left; border-bottom: 1px solid #ddd; }
          th { background-color: #f2f2f2; }
          .order-item { margin-bottom: 5px; }
          .page-break { page-break-after: always; }
          @media print {
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Liste de livraison</h1>
          <p>Date: ${new Date().toLocaleDateString()}</p>
          ${deliveryPerson ? `<p>Livreur: ${deliveryPerson.name} (${deliveryPerson.phone})</p>` : ''}
          <p>Nombre de commandes: ${ordersData.length}</p>
        </div>
        
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Client</th>
              <th>Adresse</th>
              <th>Téléphone</th>
              <th>Articles</th>
              <th>Livreur Actuel</th>
              <th>Total</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            ${ordersData.map((order, index) => `
              <tr>
                <td>${index + 1}</td>
                <td>${order.customer_name || 'N/A'}</td>
                <td>${order.delivery_address || 'N/A'}</td>
                <td>${order.customer_phone || 'N/A'}</td>
                <td>
                  ${(order.order_items || []).map(item => `
                    <div class="order-item">${item.quantity}x ${item.product_name}</div>
                  `).join('')}
                </td>
                <td>${order.delivery_person?.name || '-'}</td>
                <td>${new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF' }).format(order.total_amount || 0)}</td>
                <td>${order.delivery_notes || '-'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <div class="no-print" style="margin-top: 20px; text-align: center;">
          <button onclick="window.print()">Imprimer</button>
        </div>
      </body>
      </html>
    `;
  };

  const showMessageModal = () => {
    if (selectedOrders.length === 0) {
      message.warning('Veuillez sélectionner au moins une commande');
      return;
    }
    
    if (!selectedDeliveryPerson) {
      message.warning('Veuillez sélectionner un livreur');
      return;
    }
    
    messageForm.resetFields();
    setMessageModalVisible(true);
  };

  const handleSendMessage = async (values) => {
    try {
      setLoading(true);
      
      const selectedOrdersData = orders.filter(order => selectedOrders.includes(order.id));
      const deliveryPerson = deliveryPersons.find(p => p.id === selectedDeliveryPerson);
      
      let messageContent = values.messageTemplate;
      
      if (values.includeAddress) {
        messageContent += '\n\nAdresses de livraison:';
        selectedOrdersData.forEach((order, index) => {
          messageContent += `\n${index + 1}. ${order.customer_name}: ${order.delivery_address}`;
        });
      }
      
      if (values.includePhone) {
        messageContent += '\n\nTéléphones clients:';
        selectedOrdersData.forEach((order, index) => {
          messageContent += `\n${index + 1}. ${order.customer_name}: ${order.customer_phone}`;
        });
      }
      
      if (values.includeTotal) {
        messageContent += '\n\nMontants:';
        selectedOrdersData.forEach((order, index) => {
          messageContent += `\n${index + 1}. ${order.customer_name}: ${new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF' }).format(order.total_amount || 0)}`;
        });
      }
      
      await deliveryPersonsApi.sendMessage(deliveryPerson.id, {
        message: messageContent,
        order_ids: selectedOrders
      });
      
      message.success('Message envoyé avec succès');
      setMessageModalVisible(false);
    } catch (error) {
      console.error('Erreur lors de l\'envoi du message:', error);
      message.error('Erreur lors de l\'envoi du message');
    } finally {
      setLoading(false);
    }
  };

  // Filtrage des commandes en fonction de la recherche
  const filteredOrders = orders.filter(order => 
    (order.customer_name?.toLowerCase() || '').includes(searchText.toLowerCase()) ||
    (order.order_number?.toLowerCase() || '').includes(searchText.toLowerCase()) ||
    (order.delivery_address?.toLowerCase() || '').includes(searchText.toLowerCase()) ||
    (order.delivery_person?.name?.toLowerCase() || '').includes(searchText.toLowerCase())
  );

  // Fonction pour afficher les détails de la commande
  const showDetailsModal = async (orderId) => {
    setDetailsLoading(true);
    setDetailsModalVisible(true);
    try {
      const response = await ordersApi.getById(orderId);
      if (response && response.data) {
        setSelectedOrderDetails(response.data);
      } else {
        throw new Error('Données de commande non reçues');
      }
    } catch (error) {
      console.error('Erreur lors du chargement des détails de la commande:', error);
      message.error('Erreur lors du chargement des détails');
      setDetailsModalVisible(false); // Fermer la modale en cas d'erreur
    } finally {
      setDetailsLoading(false);
    }
  };

  // Colonnes du tableau des commandes
  const columns = [
    {
      title: 'N° Commande',
      dataIndex: 'order_number',
      key: 'order_number',
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: 'Client',
      dataIndex: 'customer_name',
      key: 'customer_name',
    },
    {
      title: 'Adresse',
      dataIndex: 'delivery_address',
      key: 'delivery_address',
      ellipsis: true,
    },
    {
      title: 'Livreur Actuel',
      dataIndex: 'delivery_person_id',
      key: 'delivery_person',
      render: (deliveryPersonId) => {
        const deliveryPerson = deliveryPersons.find(p => p.id === deliveryPersonId);
        return (
          <Text type={deliveryPerson ? 'default' : 'secondary'}>
            {deliveryPerson?.name || 'Non assigné'}
          </Text>
        );
      },
      sorter: (a, b) => {
        const nameA = deliveryPersons.find(p => p.id === a.delivery_person_id)?.name || '';
        const nameB = deliveryPersons.find(p => p.id === b.delivery_person_id)?.name || '';
        return nameA.localeCompare(nameB);
      },
    },
    {
      title: 'Total',
      dataIndex: 'total_amount',
      key: 'total_amount',
      render: (amount) => (
        <Text strong>
          {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF' }).format(amount || 0)}
        </Text>
      ),
      sorter: (a, b) => (a.total_amount || 0) - (b.total_amount || 0),
    },
    {
      title: 'Date livraison',
      dataIndex: 'delivery_date',
      key: 'delivery_date',
      render: (date) => date ? new Date(date).toLocaleDateString() : 'Non spécifiée',
      sorter: (a, b) => {
        const dateA = a.delivery_date ? new Date(a.delivery_date) : null;
        const dateB = b.delivery_date ? new Date(b.delivery_date) : null;

        // Si une seule date est null, celle qui n'est pas null vient en premier
        if (dateA && !dateB) return -1; // dateA avant dateB
        if (!dateA && dateB) return 1;  // dateB avant dateA
        if (!dateA && !dateB) return 0; // Les deux sont null, ordre indifférent

        // Si les deux dates sont valides, trier normalement (décroissant)
        return dateB - dateA;
      },
      defaultSortOrder: 'ascend', // Tri par défaut sur cette colonne
    },
    {
      title: 'Date',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => date ? new Date(date).toLocaleDateString() : 'N/A',
      sorter: (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0),
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right', // Fixer la colonne à droite si besoin
      render: (_, record) => (
        <Tooltip title="Voir détails">
          <Button 
            type="link" 
            icon={<FileTextOutlined />} 
            onClick={() => showDetailsModal(record.id)}
          />
        </Tooltip>
      ),
    },
  ];

  // Données pour les livreurs
  const deliveryPersonOptions = deliveryPersons.map(person => {
    const stats = deliveryPersonsStats[person.id] || { orders_count: 0 };
    return {
      value: person.id,
      label: (
        <Space>
          <UserOutlined />
          <span>{person.name}</span>
          <Tag color="green">{stats.orders_count} colis aujourd'hui</Tag>
        </Space>
      ),
      name: person.name,
      orders_count: stats.orders_count
    };
  });

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px' }}>
      <Title level={2}>Attribution des commandes</Title>
      
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={8} md={6}>
            <Text strong>Zone de livraison:</Text>
            <Select
              style={{ width: '100%', marginTop: 8 }}
              placeholder="Sélectionner une zone"
              value={selectedZone}
              onChange={handleZoneChange}
            >
              <Option value="all">Toutes les zones</Option>
              {zones.map(zone => (
                <Option key={zone.id} value={zone.id}>
                  {zone.name}
                </Option>
              ))}
            </Select>
          </Col>
          
          <Col xs={24} sm={16} md={10}>
            <Text strong>Rechercher:</Text>
            <Search
              placeholder="Rechercher par client, numéro ou adresse"
              allowClear
              onSearch={handleSearch}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: '100%', marginTop: 8 }}
            />
          </Col>
          
          <Col xs={24} md={8} style={{ textAlign: 'right' }}>
            <Button 
              icon={<SyncOutlined />} 
              onClick={handleRefresh}
              style={{ marginRight: 8 }}
            >
              Actualiser
            </Button>
            <Button 
              type="primary" 
              icon={<CarOutlined />}
              onClick={showAssignmentModal}
              disabled={selectedOrders.length === 0}
            >
              Attribuer
            </Button>
          </Col>
        </Row>
      </Card>
      
      <Row gutter={16}>
        <Col xs={24} lg={18}>
          <Card title={<Space><InboxOutlined /> Nouvelles commandes à attribuer</Space>}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Spin size="large" />
              </div>
            ) : (
              <Table
                rowSelection={{
                  type: 'checkbox',
                  selectedRowKeys: selectedOrders,
                  onChange: handleOrderSelection,
                }}
                columns={columns}
                dataSource={filteredOrders.map(order => ({ ...order, key: order.id }))}
                pagination={{ pageSize: 10 }}
                size="middle"
                scroll={{ x: 'max-content' }}
              />
            )}
          </Card>
        </Col>
        
        <Col xs={24} lg={6}>
          <Card title={<Space><UserOutlined /> Livreurs disponibles</Space>}>
            {deliveryPersonOptions.length > 0 ? (
              <div>
                {deliveryPersonOptions.map(option => (
                  <div key={option.value} style={{ marginBottom: 12 }}>
                    <Button
                      block
                      type={selectedDeliveryPerson === option.value ? 'primary' : 'default'}
                      onClick={() => handleDeliveryPersonSelection(option.value)}
                      style={{ textAlign: 'left', height: 'auto', padding: '8px 16px' }}
                    >
                      <Space direction="vertical" size={0} style={{ width: '100%' }}>
                        <Text strong>{option.name}</Text>
                        <Text type="secondary">
                          <Badge 
                            status={option.orders_count > 10 ? 'warning' : 'success'} 
                            text={`${option.orders_count} colis aujourd'hui`}
                          />
                        </Text>
                      </Space>
                    </Button>
                  </div>
                ))}
                
                <Divider />
                
                <Space style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                  <Tooltip title="Imprimer la liste de livraison">
                    <Button 
                      icon={<PrinterOutlined />} 
                      disabled={selectedOrders.length === 0}
                      onClick={showPrintModal}
                    >
                      Imprimer
                    </Button>
                  </Tooltip>
                  
                  <Tooltip title="Envoyer les informations au livreur">
                    <Button 
                      icon={<SendOutlined />} 
                      disabled={selectedOrders.length === 0 || !selectedDeliveryPerson}
                      onClick={showMessageModal}
                    >
                      Message
                    </Button>
                  </Tooltip>
                </Space>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                {loading ? <Spin size="small" /> : "Aucun livreur disponible"}
              </div>
            )}
          </Card>
        </Col>
      </Row>
      
      {/* Modal d'attribution */}
      <Modal
        title="Attribuer des commandes"
        visible={assignmentModalVisible}
        onCancel={() => setAssignmentModalVisible(false)}
        onOk={handleAssign}
        okText="Attribuer"
        cancelText="Annuler"
        confirmLoading={loading}
      >
        <Form 
          form={assignmentForm}
          layout="vertical"
          initialValues={{
            delivery_person_id: selectedDeliveryPerson,
            delivery_date: '',
            shipping_fee: 0
          }}
        >
          <Form.Item
            label="Livreur"
            name="delivery_person_id"
            rules={[{ required: true, message: 'Veuillez sélectionner un livreur' }]}
          >
            <Select
              placeholder="Sélectionner un livreur"
              optionLabelProp="label"
            >
              {deliveryPersonOptions.map(option => (
                <Option key={option.value} value={option.value} label={option.name}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Date de livraison"
            name="delivery_date"
            tooltip="Laissez vide pour utiliser la date par défaut de chaque commande"
          >
            <DatePicker 
              style={{ width: '100%' }} 
              format="DD/MM/YYYY"
              placeholder="Sélectionner une date (optionnel)"
              disabledDate={(current) => {
                // Désactiver les dates avant aujourd'hui et après 7 jours
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                
                const maxDate = new Date();
                maxDate.setDate(maxDate.getDate() + 7);
                maxDate.setHours(23, 59, 59, 999);
                
                return current && (current < today || current > maxDate);
              }}
            />
          </Form.Item>

          <Form.Item
            label="Frais de livraison"
            name="shipping_fee"
            tooltip="Frais de livraison à appliquer (0 pour utiliser les frais par défaut)"
          >
            <Input 
              type="number" 
              min={0} 
              step={500}
              addonAfter="XOF"
              placeholder="Frais de livraison"
            />
          </Form.Item>

          <div style={{ marginBottom: 16 }}>
            <Text>Attribution de {selectedOrders.length} commande(s)</Text>
          </div>
        </Form>
      </Modal>
      
      {/* Modal d'impression */}
      <Modal
        title="Imprimer la liste de livraison"
        visible={printModalVisible}
        onCancel={() => setPrintModalVisible(false)}
        onOk={handlePrint}
        okText="Imprimer"
        cancelText="Annuler"
      >
        <div>
          <Text>Vous êtes sur le point d'imprimer une liste de livraison contenant {selectedOrders.length} commande(s).</Text>
          
          {!selectedDeliveryPerson && (
            <div style={{ marginTop: 16 }}>
              <Text type="warning">Aucun livreur n'est sélectionné. La liste sera générique.</Text>
              <div style={{ marginTop: 8 }}>
                <Select
                  style={{ width: '100%' }}
                  placeholder="Sélectionner un livreur (optionnel)"
                  value={selectedDeliveryPerson}
                  onChange={handleDeliveryPersonSelection}
                  optionLabelProp="label"
                >
                  {deliveryPersonOptions.map(option => (
                    <Option key={option.value} value={option.value} label={option.name}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </div>
            </div>
          )}
        </div>
      </Modal>
      
      {/* Modal d'envoi de message */}
      <Modal
        title="Envoyer un message au livreur"
        visible={messageModalVisible}
        onCancel={() => setMessageModalVisible(false)}
        footer={null}
      >
        <Form
          form={messageForm}
          layout="vertical"
          onFinish={handleSendMessage}
          initialValues={{
            messageTemplate: `Bonjour, vous avez ${selectedOrders.length} nouvelle(s) commande(s) à livrer.`,
            includeAddress: true,
            includePhone: true,
            includeTotal: true
          }}
        >
          <Form.Item
            name="messageTemplate"
            label="Texte du message"
            rules={[{ required: true, message: 'Veuillez saisir le message' }]}
          >
            <Input.TextArea rows={4} />
          </Form.Item>
          
          <Form.Item name="includeAddress" valuePropName="checked">
            <Checkbox>Inclure les adresses</Checkbox>
          </Form.Item>
          
          <Form.Item name="includePhone" valuePropName="checked">
            <Checkbox>Inclure les téléphones</Checkbox>
          </Form.Item>
          
          <Form.Item name="includeTotal" valuePropName="checked">
            <Checkbox>Inclure les montants</Checkbox>
          </Form.Item>
          
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} icon={<SendOutlined />}>
              Envoyer
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal des détails de la commande */}
      <Modal
        title={`Détails de la commande N° ${selectedOrderDetails?.order_number}`}
        visible={detailsModalVisible}
        onCancel={() => setDetailsModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailsModalVisible(false)}>
            Fermer
          </Button>
        ]}
        width={800} // Ajuster la largeur si nécessaire
      >
        {detailsLoading ? (
          <Spin />
        ) : selectedOrderDetails ? (
          <div>
            <Row gutter={16}>
              <Col span={12}>
                <Title level={5}>Informations Client</Title>
                <Text><strong>Nom:</strong> {selectedOrderDetails.customer_name || 'N/A'}</Text><br />
                <Text><strong>Téléphone:</strong> {selectedOrderDetails.customer_phone || 'N/A'}</Text><br />
                <Text><strong>Adresse:</strong> {selectedOrderDetails.delivery_address || 'N/A'}</Text><br />
                <Text><strong>Notes livraison:</strong> {selectedOrderDetails.delivery_notes || 'Aucune'}</Text>
              </Col>
              <Col span={12}>
                <Title level={5}>Informations Commande</Title>
                <Text><strong>Date commande:</strong> {selectedOrderDetails.created_at ? new Date(selectedOrderDetails.created_at).toLocaleString() : 'N/A'}</Text><br />
                <Text><strong>Date livraison prévue:</strong> {selectedOrderDetails.delivery_date ? new Date(selectedOrderDetails.delivery_date).toLocaleDateString() : 'Non spécifiée'}</Text><br />
                <Text><strong>Statut:</strong> <Tag>{selectedOrderDetails.status}</Tag></Text><br />
                <Text><strong>Montant Total:</strong> {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF' }).format(selectedOrderDetails.total_amount || 0)}</Text>
              </Col>
            </Row>
            <Divider />
            <Title level={5}>Articles de la commande</Title>
            <Table
              dataSource={selectedOrderDetails.order_items?.map(item => ({ ...item, key: item.id }))}
              columns={[
                { title: 'Produit', dataIndex: 'product_name', key: 'product_name' },
                { title: 'Quantité', dataIndex: 'quantity', key: 'quantity' },
                { title: 'Prix Unitaire', dataIndex: 'product_price', key: 'product_price', render: price => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF' }).format(price || 0) },
                { title: 'Total Article', key: 'total', render: (_, item) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF' }).format((item.product_price || 0) * (item.quantity || 0)) },
              ]}
              pagination={false}
              size="small"
            />
          </div>
        ) : (
          <Text>Aucun détail à afficher.</Text>
        )}
      </Modal>
    </div>
  );
};

export default OrderAssignment; 